import { useQuery } from "@tanstack/react-query";
import {
  agentToolsApi,
  type MCPConnectionTestResult,
} from "../../../services/agent-tools-service";
import { MCP_CONNECTIONS_QUERY_KEY } from "./useMCPConnections";
import type { LiveStatus } from "../components/StatusDot";

export const MCP_CONNECTION_TEST_QUERY_KEY = [
  ...MCP_CONNECTIONS_QUERY_KEY,
  "test",
] as const;

type AxiosLikeError = {
  response?: {
    status?: number;
    statusText?: string;
    data?: {
      detail?: string;
      message?: string;
      error?: { message?: string };
    };
  };
  message?: string;
};

// Network/HTTP errors from axios don't share the same shape as the in-body
// `{ ok: false, error }` payload, so dig for whichever message is present.
const extractErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const e = error as AxiosLikeError;
  const status = e.response?.status;
  const detail =
    e.response?.data?.error?.message ||
    e.response?.data?.detail ||
    e.response?.data?.message ||
    e.response?.statusText ||
    e.message;
  if (status && detail) return `${status} — ${detail}`;
  if (status) return `Request failed with status ${status}`;
  return detail ?? null;
};

type UseMCPConnectionTestResult = {
  status: LiveStatus;
  result: MCPConnectionTestResult | undefined;
  errorMessage: string | null;
  authRequired: boolean;
  isTesting: boolean;
  refetch: () => void;
};

export const useMCPConnectionTest = (
  id: string | null,
): UseMCPConnectionTestResult => {
  const query = useQuery({
    queryKey: [...MCP_CONNECTION_TEST_QUERY_KEY, id],
    queryFn: () => agentToolsApi.testMCPConnection(id as string),
    enabled: Boolean(id),
    // 30s of cache shares results between the list dots and the drawer header
    // without re-firing per render. The drawer's Refresh button forces fresh
    // via `refetch()` whenever the user wants a real-time check.
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const hasError = Boolean(query.error);
  const hasResult = Boolean(query.data);

  // Order matters:
  //   1. transport/HTTP error (401, network down, etc.)  → offline
  //   2. successful response that itself says !ok        → offline
  //   3. successful response with ok                     → online
  //   4. otherwise (no data yet, request in flight)      → testing
  let status: LiveStatus;
  if (hasError) {
    status = "offline";
  } else if (hasResult) {
    status = query.data!.ok ? "online" : "offline";
  } else {
    status = "testing";
  }

  const errorMessage = hasError
    ? extractErrorMessage(query.error)
    : query.data && !query.data.ok
      ? query.data.error
      : null;

  return {
    status,
    result: query.data,
    errorMessage,
    authRequired: query.data?.authRequired ?? false,
    isTesting: query.isFetching,
    refetch: () => {
      query.refetch();
    },
  };
};
